import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import type { FilterState } from "../types";

/**
 * Hook to manage filter state with URL param synchronization
 * Allows filters to be shareable via URL and persist across page refreshes
 */
export function useFilterState() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Parse filter state from URL params
  const filterState: FilterState = useMemo(() => {
    const statusParam = searchParams.get("status");
    const genresParam = searchParams.get("genres");
    const yearMinParam = searchParams.get("yearMin");
    const yearMaxParam = searchParams.get("yearMax");

    return {
      status: (statusParam as "all" | "published" | "draft") || "all",
      genres: genresParam ? genresParam.split(",") : [],
      yearRange: [
        yearMinParam ? Number.parseInt(yearMinParam, 10) : 1900,
        yearMaxParam ? Number.parseInt(yearMaxParam, 10) : new Date().getFullYear(),
      ],
    };
  }, [searchParams]);

  // Update filter state and sync to URL
  const updateFilters = useCallback(
    (updates: Partial<FilterState>) => {
      const newParams = new URLSearchParams(searchParams);

      // Update status
      if (updates.status !== undefined) {
        if (updates.status === "all") {
          newParams.delete("status");
        } else {
          newParams.set("status", updates.status);
        }
      }

      // Update genres
      if (updates.genres !== undefined) {
        if (updates.genres.length === 0) {
          newParams.delete("genres");
        } else {
          newParams.set("genres", updates.genres.join(","));
        }
      }

      // Update year range
      if (updates.yearRange !== undefined) {
        const [min, max] = updates.yearRange;
        if (min === 1900 && max === new Date().getFullYear()) {
          newParams.delete("yearMin");
          newParams.delete("yearMax");
        } else {
          newParams.set("yearMin", min.toString());
          newParams.set("yearMax", max.toString());
        }
      }

      setSearchParams(newParams);
    },
    [searchParams, setSearchParams],
  );

  // Clear all filters
  const clearFilters = useCallback(() => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete("status");
    newParams.delete("genres");
    newParams.delete("yearMin");
    newParams.delete("yearMax");
    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return (
      filterState.status !== "all" ||
      filterState.genres.length > 0 ||
      filterState.yearRange[0] !== 1900 ||
      filterState.yearRange[1] !== new Date().getFullYear()
    );
  }, [filterState]);

  return {
    filterState,
    updateFilters,
    clearFilters,
    hasActiveFilters,
  };
}
